// Attorney Portal API Client
// BP-10-04: Attorney Frontend

import apiClient from '@/core/lib/api-client';
import type {
  AttorneyProfile,
  AttorneyProfileFormData,
  LegalCase,
  LegalCaseFormData,
  CaseFilters,
  PaginatedCases,
  CaseEvent,
  CaseEventFormData,
  CaseTimelineResponse,
  LifeCarePlan,
  LifeCarePlanFormData,
  CostCategoryFormData,
  SettlementStructure,
  SettlementCalculation,
  SettlementScenario,
  SharedDocument,
  AttorneyDashboardStats,
  ClientCaseView,
  CaseMessage,
} from '@/features/attorney/types/attorney';

// ============================================================================
// Attorney Profile
// ============================================================================

export const attorneyProfileApi = {
  /**
   * Get the current attorney's profile
   */
  getProfile: async (): Promise<AttorneyProfile> => {
    const response = await apiClient.get<AttorneyProfile>('/attorney/profile');
    return response.data;
  },

  /**
   * Update attorney profile
   */
  updateProfile: async (data: AttorneyProfileFormData): Promise<AttorneyProfile> => {
    const response = await apiClient.patch<AttorneyProfile>('/attorney/profile', data);
    return response.data;
  },

  /**
   * Upload profile photo
   */
  uploadPhoto: async (file: File): Promise<{ photoUrl: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post<{ photoUrl: string }>(
      '/attorney/profile/photo',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  /**
   * Submit verification documents
   */
  submitVerification: async (documents: File[]): Promise<{ message: string }> => {
    const formData = new FormData();
    documents.forEach((doc, index) => {
      formData.append(`documents[${index}]`, doc);
    });
    const response = await apiClient.post<{ message: string }>(
      '/attorney/verify',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },
};

// ============================================================================
// Legal Cases
// ============================================================================

export const casesApi = {
  /**
   * Get all cases for the attorney
   */
  getCases: async (
    page = 1,
    pageSize = 20,
    filters?: CaseFilters
  ): Promise<PaginatedCases> => {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });

    if (filters?.status?.length) {
      filters.status.forEach((s) => params.append('status', s));
    }
    if (filters?.caseType) params.append('case_type', filters.caseType);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.dateFrom) params.append('date_from', filters.dateFrom);
    if (filters?.dateTo) params.append('date_to', filters.dateTo);

    const response = await apiClient.get<PaginatedCases>(
      `/attorney/cases?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Get a single case by ID
   */
  getCase: async (caseId: string): Promise<LegalCase> => {
    const response = await apiClient.get<LegalCase>(`/attorney/cases/${caseId}`);
    return response.data;
  },

  /**
   * Create a new case
   */
  createCase: async (data: LegalCaseFormData): Promise<LegalCase> => {
    const response = await apiClient.post<LegalCase>('/attorney/cases', data);
    return response.data;
  },

  /**
   * Update a case
   */
  updateCase: async (caseId: string, data: Partial<LegalCaseFormData>): Promise<LegalCase> => {
    const response = await apiClient.patch<LegalCase>(`/attorney/cases/${caseId}`, data);
    return response.data;
  },

  /**
   * Archive/delete a case
   */
  archiveCase: async (caseId: string): Promise<void> => {
    await apiClient.delete(`/attorney/cases/${caseId}`);
  },

  /**
   * Link a family to a case
   */
  linkFamily: async (caseId: string, familyId: string): Promise<LegalCase> => {
    const response = await apiClient.post<LegalCase>(
      `/attorney/cases/${caseId}/link-family`,
      { family_id: familyId }
    );
    return response.data;
  },

  /**
   * Unlink a family from a case
   */
  unlinkFamily: async (caseId: string): Promise<LegalCase> => {
    const response = await apiClient.delete<LegalCase>(
      `/attorney/cases/${caseId}/unlink-family`
    );
    return response.data;
  },

  /**
   * Get case summary report
   */
  getCaseSummary: async (caseId: string): Promise<Blob> => {
    const response = await apiClient.get(`/attorney/cases/${caseId}/summary`, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Get dashboard statistics
   */
  getDashboardStats: async (): Promise<AttorneyDashboardStats> => {
    const response = await apiClient.get<AttorneyDashboardStats>('/attorney/dashboard/stats');
    return response.data;
  },
};

// ============================================================================
// Case Events
// ============================================================================

export const caseEventsApi = {
  /**
   * Get events for a case
   */
  getEvents: async (
    caseId: string,
    page = 1,
    pageSize = 50
  ): Promise<CaseTimelineResponse> => {
    const response = await apiClient.get<CaseTimelineResponse>(
      `/cases/${caseId}/events?page=${page}&page_size=${pageSize}`
    );
    return response.data;
  },

  /**
   * Get a single event
   */
  getEvent: async (eventId: string): Promise<CaseEvent> => {
    const response = await apiClient.get<CaseEvent>(`/case-events/${eventId}`);
    return response.data;
  },

  /**
   * Create a new event
   */
  createEvent: async (caseId: string, data: CaseEventFormData): Promise<CaseEvent> => {
    const response = await apiClient.post<CaseEvent>(`/cases/${caseId}/events`, data);
    return response.data;
  },

  /**
   * Update an event
   */
  updateEvent: async (eventId: string, data: Partial<CaseEventFormData>): Promise<CaseEvent> => {
    const response = await apiClient.patch<CaseEvent>(`/case-events/${eventId}`, data);
    return response.data;
  },

  /**
   * Delete an event
   */
  deleteEvent: async (eventId: string): Promise<void> => {
    await apiClient.delete(`/case-events/${eventId}`);
  },

  /**
   * Mark event as completed
   */
  completeEvent: async (eventId: string): Promise<CaseEvent> => {
    const response = await apiClient.post<CaseEvent>(`/case-events/${eventId}/complete`);
    return response.data;
  },

  /**
   * Upload attachment to event
   */
  uploadAttachment: async (eventId: string, file: File): Promise<CaseEvent> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post<CaseEvent>(
      `/case-events/${eventId}/attachments`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },
};

// ============================================================================
// Life Care Plans
// ============================================================================

export const lifeCarePlansApi = {
  /**
   * Get life care plans for a case
   */
  getPlans: async (caseId: string): Promise<LifeCarePlan[]> => {
    const response = await apiClient.get<LifeCarePlan[]>(`/cases/${caseId}/life-care-plans`);
    return response.data;
  },

  /**
   * Get a single life care plan
   */
  getPlan: async (planId: string): Promise<LifeCarePlan> => {
    const response = await apiClient.get<LifeCarePlan>(`/life-care-plans/${planId}`);
    return response.data;
  },

  /**
   * Create a new life care plan
   */
  createPlan: async (caseId: string, data: LifeCarePlanFormData): Promise<LifeCarePlan> => {
    const response = await apiClient.post<LifeCarePlan>(
      `/cases/${caseId}/life-care-plans`,
      data
    );
    return response.data;
  },

  /**
   * Update a life care plan
   */
  updatePlan: async (
    planId: string,
    data: Partial<LifeCarePlanFormData>
  ): Promise<LifeCarePlan> => {
    const response = await apiClient.patch<LifeCarePlan>(`/life-care-plans/${planId}`, data);
    return response.data;
  },

  /**
   * Add cost category to plan
   */
  addCostCategory: async (
    planId: string,
    categoryType: string,
    data: CostCategoryFormData
  ): Promise<LifeCarePlan> => {
    const response = await apiClient.post<LifeCarePlan>(
      `/life-care-plans/${planId}/costs/${categoryType}`,
      data
    );
    return response.data;
  },

  /**
   * Update cost category
   */
  updateCostCategory: async (
    planId: string,
    categoryType: string,
    costId: string,
    data: Partial<CostCategoryFormData>
  ): Promise<LifeCarePlan> => {
    const response = await apiClient.patch<LifeCarePlan>(
      `/life-care-plans/${planId}/costs/${categoryType}/${costId}`,
      data
    );
    return response.data;
  },

  /**
   * Remove cost category
   */
  removeCostCategory: async (
    planId: string,
    categoryType: string,
    costId: string
  ): Promise<LifeCarePlan> => {
    const response = await apiClient.delete<LifeCarePlan>(
      `/life-care-plans/${planId}/costs/${categoryType}/${costId}`
    );
    return response.data;
  },

  /**
   * Recalculate plan totals
   */
  recalculate: async (planId: string): Promise<LifeCarePlan> => {
    const response = await apiClient.post<LifeCarePlan>(
      `/life-care-plans/${planId}/recalculate`
    );
    return response.data;
  },

  /**
   * Export plan as PDF
   */
  exportPdf: async (planId: string): Promise<Blob> => {
    const response = await apiClient.get(`/life-care-plans/${planId}/export/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Delete a life care plan
   */
  deletePlan: async (planId: string): Promise<void> => {
    await apiClient.delete(`/life-care-plans/${planId}`);
  },
};

// ============================================================================
// Settlement Planning
// ============================================================================

export const settlementApi = {
  /**
   * Calculate settlement distribution
   */
  calculate: async (
    caseId: string,
    data: {
      grossAmount: number;
      attorneyFeePercentage: number;
      litigationCosts: number;
      medicalLiens: number;
      otherDeductions: number;
    }
  ): Promise<SettlementCalculation> => {
    const response = await apiClient.post<SettlementCalculation>(
      `/attorney/cases/${caseId}/settlement/calculate`,
      data
    );
    return response.data;
  },

  /**
   * Save settlement structure
   */
  saveStructure: async (
    caseId: string,
    structure: SettlementStructure
  ): Promise<LegalCase> => {
    const response = await apiClient.post<LegalCase>(
      `/attorney/cases/${caseId}/settlement/structure`,
      structure
    );
    return response.data;
  },

  /**
   * Get saved settlement scenarios
   */
  getScenarios: async (caseId: string): Promise<SettlementScenario[]> => {
    const response = await apiClient.get<SettlementScenario[]>(
      `/attorney/cases/${caseId}/settlement/scenarios`
    );
    return response.data;
  },

  /**
   * Save a settlement scenario
   */
  saveScenario: async (
    caseId: string,
    name: string,
    calculation: SettlementCalculation,
    structure: SettlementStructure
  ): Promise<SettlementScenario> => {
    const response = await apiClient.post<SettlementScenario>(
      `/attorney/cases/${caseId}/settlement/scenarios`,
      { name, calculation, structure }
    );
    return response.data;
  },

  /**
   * Delete a settlement scenario
   */
  deleteScenario: async (caseId: string, scenarioId: string): Promise<void> => {
    await apiClient.delete(`/attorney/cases/${caseId}/settlement/scenarios/${scenarioId}`);
  },

  /**
   * Calculate present value
   */
  calculatePresentValue: async (
    futureValue: number,
    years: number,
    discountRate: number
  ): Promise<{ presentValue: number }> => {
    const response = await apiClient.post<{ presentValue: number }>(
      '/attorney/settlement/present-value',
      { future_value: futureValue, years, discount_rate: discountRate }
    );
    return response.data;
  },
};

// ============================================================================
// Shared Documents
// ============================================================================

export const sharedDocumentsApi = {
  /**
   * Get shared documents for a case
   */
  getDocuments: async (caseId: string): Promise<SharedDocument[]> => {
    const response = await apiClient.get<SharedDocument[]>(
      `/attorney/cases/${caseId}/shared-documents`
    );
    return response.data;
  },

  /**
   * Download a shared document
   */
  downloadDocument: async (documentId: string): Promise<{ url: string }> => {
    const response = await apiClient.get<{ url: string }>(
      `/documents/${documentId}/download`
    );
    return response.data;
  },
};

// ============================================================================
// Client Portal (Family View)
// ============================================================================

export const clientPortalApi = {
  /**
   * Get case view for family
   */
  getCaseView: async (): Promise<ClientCaseView | null> => {
    const response = await apiClient.get<ClientCaseView>('/family/legal-case');
    return response.data;
  },

  /**
   * Get messages for case
   */
  getMessages: async (caseId: string): Promise<CaseMessage[]> => {
    const response = await apiClient.get<CaseMessage[]>(
      `/family/legal-case/${caseId}/messages`
    );
    return response.data;
  },

  /**
   * Send message to attorney
   */
  sendMessage: async (caseId: string, content: string): Promise<CaseMessage> => {
    const response = await apiClient.post<CaseMessage>(
      `/family/legal-case/${caseId}/messages`,
      { content }
    );
    return response.data;
  },

  /**
   * Mark messages as read
   */
  markMessagesRead: async (caseId: string, messageIds: string[]): Promise<void> => {
    await apiClient.post(`/family/legal-case/${caseId}/messages/read`, {
      message_ids: messageIds,
    });
  },
};

// ============================================================================
// Expense Reports (for case)
// ============================================================================

export const caseExpensesApi = {
  /**
   * Get expense report for a case
   */
  getExpenseReport: async (
    caseId: string,
    dateFrom?: string,
    dateTo?: string
  ): Promise<{
    expenses: Array<{
      id: string;
      category: string;
      description: string;
      amount: number;
      date: string;
      isInsuranceCovered: boolean;
      outOfPocket: number;
    }>;
    summary: {
      totalExpenses: number;
      totalInsuranceCovered: number;
      totalOutOfPocket: number;
      byCategory: Record<string, number>;
    };
  }> => {
    const params = new URLSearchParams();
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);

    const response = await apiClient.get(
      `/attorney/cases/${caseId}/expense-report?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Export expense report
   */
  exportReport: async (
    caseId: string,
    format: 'pdf' | 'csv' | 'excel'
  ): Promise<Blob> => {
    const response = await apiClient.get(
      `/attorney/cases/${caseId}/expense-report/export?format=${format}`,
      { responseType: 'blob' }
    );
    return response.data;
  },
};

